import React, { useState, useEffect } from 'react';
import { Card, Steps, ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import { FileOutlined, DatabaseOutlined, LinkOutlined, SendOutlined } from '@ant-design/icons';
import FileUploadWidget from './components/FileUploadWidget';
import TemplateSelection from '../components/TemplateSelection';
import ColumnMapping from '../components/ColumnMapping';
import FileProcessingWidget from './components/FileProcessingWidget';

const { Step } = Steps;

/**
 * FileUploaderWidget - A self-contained widget for file upload and processing
 * 
 * Props:
 * @param {string} apiBaseUrl - Base URL for the API (required)
 * @param {string} authToken - Authentication token (required)
 * @param {Object} templates - Templates configuration object (optional, will be fetched if not provided)
 * @param {Function} onComplete - Callback when processing is complete (optional)
 * @param {Function} onError - Callback for errors (optional)
 * @param {Object} theme - Custom theme configuration (optional)
 * @param {string} className - CSS class name for the widget container (optional)
 * @param {Object} style - Inline styles for the widget container (optional)
 */
const FileUploaderWidget = ({
    apiBaseUrl,
    authToken,
    templates: externalTemplates,
    onComplete,
    onError,
    theme,
    className,
    style,
    ...props
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [fileColumns, setFileColumns] = useState([]);
    const [templates, setTemplates] = useState(externalTemplates || {});
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [columnMappings, setColumnMappings] = useState([]);

    // Validate required props
    useEffect(() => {
        if (!apiBaseUrl) {
            const error = 'FileUploaderWidget: apiBaseUrl is required';
            console.error(error);
            onError && onError(new Error(error));
        }
        if (!authToken) {
            const error = 'FileUploaderWidget: authToken is required';
            console.error(error);
            onError && onError(new Error(error));
        }
    }, [apiBaseUrl, authToken, onError]);

    // Load templates if not provided externally
    useEffect(() => {
        if (!externalTemplates && apiBaseUrl && authToken) {
            loadTemplates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- loadTemplates stable for apiBaseUrl/authToken
    }, [externalTemplates, apiBaseUrl, authToken]);

    const loadTemplates = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/templates`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setTemplates(data.templates);
        } catch (error) {
            console.error('Failed to load templates:', error);
            onError && onError(error);
        }
    };

    const handleFileUpload = (file, preview, columns) => {
        setUploadedFile(file);
        setFilePreview(preview);
        setFileColumns(columns);
        setCurrentStep(1);
    };

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        setCurrentStep(2);
    };

    const handleMappingComplete = (mappings) => {
        setColumnMappings(mappings);
        setCurrentStep(3);
    };

    const handleProcessingComplete = (outputFile) => {
        onComplete && onComplete({
            success: true,
            outputFile,
            uploadedFile,
            selectedTemplate,
            columnMappings
        });
    };

    const handleReset = () => {
        setCurrentStep(0);
        setUploadedFile(null);
        setFilePreview(null);
        setFileColumns([]);
        setSelectedTemplate(null);
        setColumnMappings([]);
    };

    const steps = [
        {
            title: 'Upload File',
            icon: <FileOutlined />,
        },
        {
            title: 'Select Template',
            icon: <DatabaseOutlined />,
        },
        {
            title: 'Map Columns',
            icon: <LinkOutlined />,
        },
        {
            title: 'Process & Send',
            icon: <SendOutlined />,
        },
    ];

    const defaultTheme = {
        token: {
            colorPrimary: '#1890ff',
        },
    };

    return (
        <ConfigProvider
            locale={enUS}
            theme={theme || defaultTheme}
        >
            <Card
                className={className}
                style={{
                    maxWidth: 800,
                    margin: '0 auto',
                    ...style
                }}
                {...props}
            >
                <Steps current={currentStep} style={{ marginBottom: 24 }}>
                    {steps.map((step, index) => (
                        <Step
                            key={index}
                            title={step.title}
                            icon={step.icon}
                        />
                    ))}
                </Steps>

                {currentStep === 0 && (
                    <FileUploadWidget
                        onFileUpload={handleFileUpload}
                        filePreview={filePreview}
                        fileColumns={fileColumns}
                        apiBaseUrl={apiBaseUrl}
                        authToken={authToken}
                        templates={templates}
                        onTemplatesLoad={setTemplates}
                    />
                )}

                {currentStep === 1 && (
                    <TemplateSelection
                        templates={templates}
                        selectedTemplate={selectedTemplate}
                        onTemplateSelect={handleTemplateSelect}
                    />
                )}

                {currentStep === 2 && (
                    <ColumnMapping
                        selectedTemplate={selectedTemplate}
                        fileColumns={fileColumns}
                        onMappingComplete={handleMappingComplete}
                    />
                )}

                {currentStep === 3 && (
                    <FileProcessingWidget
                        uploadedFile={uploadedFile}
                        selectedTemplate={selectedTemplate}
                        columnMappings={columnMappings}
                        onProcessingComplete={handleProcessingComplete}
                        onReset={handleReset}
                        apiBaseUrl={apiBaseUrl}
                        authToken={authToken}
                    />
                )}
            </Card>
        </ConfigProvider>
    );
};

export default FileUploaderWidget;
